import React, { useState, useEffect } from 'react';
import { doc, getDoc, getFirestore, updateDoc } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL, getStorage, deleteObject } from 'firebase/storage';
import firebaseApp from "../firebase/credenciales";
import { 
  FaEdit, 
  FaSave, 
  FaTimes, 
  FaUpload, 
  FaTrash, 
  FaBuilding, 
  FaMapMarkerAlt, 
  FaImage,
  FaCheckCircle,
  FaTimesCircle,
  FaExclamationTriangle,
  FaInfoCircle
} from 'react-icons/fa';

const db = getFirestore(firebaseApp);
const storage = getStorage(firebaseApp);

const Company = ({ usuario }) => {
  const rolNormalizado = String(usuario?.rol || "").toLowerCase();
  const puedeEditarEmpresa = rolNormalizado === "admin" || rolNormalizado === "superadmin";

  const [companyData, setCompanyData] = useState({
    departamento: '',
    direccion: '',
    distrito: '',
    logo: '',
    name: '',
    nombreComercial: '',
    provincia: '',
    ruc: ''
  });

  const [isEditing, setIsEditing] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [logoFile, setLogoFile] = useState(null);
  const [previewLogo, setPreviewLogo] = useState('');

  // Estados para alertas
  const [alert, setAlert] = useState({ show: false, type: '', message: '' });

  // Cargar datos de la empresa
  useEffect(() => {
    loadCompanyData();
  }, []);

  // Función para mostrar alertas
  const showAlert = (type, message) => {
    setAlert({ show: true, type, message });
    setTimeout(() => {
      setAlert({ show: false, type: '', message: '' });
    }, 5000);
  };

  const loadCompanyData = async () => {
    try {
      setLoading(true);
      const docRef = doc(db, 'company', usuario.ruc); // Usar RUC como ID o uno por defecto
      const docSnap = await getDoc(docRef);
      
      if (docSnap.exists()) {
        const data = docSnap.data();
        setCompanyData(data);
        setPreviewLogo(data.logo || '');
      } else {
        showAlert('info', 'No se encontraron datos de la empresa. Puede crear un nuevo registro.');
      }
    } catch (error) {
      console.error('Error al cargar datos de la empresa:', error);
      showAlert('danger', 'Error al cargar los datos de la empresa');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setCompanyData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleLogoChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Validar tipo de archivo
      if (!file.type.startsWith('image/')) {
        showAlert('warning', 'Por favor seleccione un archivo de imagen válido');
        return;
      }
      
      // Validar tamaño (máximo 5MB)
      if (file.size > 5 * 1024 * 1024) {
        showAlert('warning', 'El archivo es demasiado grande. Máximo 5MB permitido');
        return;
      }

      setLogoFile(file);
      
      // Crear preview
      const reader = new FileReader();
      reader.onload = (e) => {
        setPreviewLogo(e.target.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const uploadLogo = async () => {
    if (!logoFile) return companyData.logo;

    try {
      setUploadingLogo(true);
      
      // Eliminar logo anterior si existe
      if (companyData.logo) {
        try {
          const oldLogoRef = ref(storage, companyData.logo);
          await deleteObject(oldLogoRef);
        } catch (error) {
          console.log('No se pudo eliminar el logo anterior:', error);
        }
      }

      // Subir nuevo logo
      const logoRef = ref(storage, `logos/logo_${companyData.ruc}_${Date.now()}.${logoFile.name.split('.').pop()}`);
      const snapshot = await uploadBytes(logoRef, logoFile);
      const downloadURL = await getDownloadURL(snapshot.ref);
      
      return downloadURL;
    } catch (error) {
      console.error('Error al subir logo:', error);
      showAlert('danger', 'Error al subir el logo');
      return companyData.logo;
    } finally {
      setUploadingLogo(false);
    }
  };

  const handleSave = async () => {
    if (!puedeEditarEmpresa) {
      showAlert('warning', 'Solo usuarios administradores pueden actualizar la información de la empresa');
      setIsEditing(false);
      return;
    }

    try {
      setSaving(true);

      // Validaciones básicas
      if (!companyData.ruc || !companyData.name) {
        showAlert('warning', 'RUC y Nombre de la empresa son campos obligatorios');
        return;
      }

      // Subir logo si hay uno nuevo
      let logoURL = companyData.logo;
      if (logoFile) {
        logoURL = await uploadLogo();
      }

      const updatedData = {
        ...companyData,
        logo: logoURL
      };

      // Guardar en Firestore usando el RUC como ID del documento
      const docRef = doc(db, 'company', companyData.ruc);
      await updateDoc(docRef, updatedData);

      setCompanyData(updatedData);
      setIsEditing(false);
      setLogoFile(null);
      showAlert('success', 'Datos de la empresa actualizados correctamente');
      
    } catch (error) {
      console.error('Error al guardar:', error);
      showAlert('danger', 'Error al guardar los datos de la empresa');
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    setIsEditing(false);
    setLogoFile(null);
    setPreviewLogo(companyData.logo || '');
    // Recargar datos originales
    loadCompanyData();
  };

  const handleRemoveLogo = () => {
    setLogoFile(null);
    setPreviewLogo('');
    setCompanyData(prev => ({
      ...prev,
      logo: ''
    }));
  };

  const getAlertIcon = (type) => {
    switch (type) {
      case 'success':
        return <FaCheckCircle className="me-2" />;
      case 'warning':
        return <FaExclamationTriangle className="me-2" />;
      case 'danger':
        return <FaTimesCircle className="me-2" />;
      case 'info':
        return <FaInfoCircle className="me-2" />;
      default:
        return <FaInfoCircle className="me-2" />;
    }
  };

  if (loading) {
    return (
      <div className="container-fluid py-4">
        <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '400px' }}>
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Cargando...</span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="container-fluid py-4">
      <div className="row">
        <div className="col-12">
          {/* Alerta Bootstrap */}
          {alert.show && (
            <div className={`alert alert-${alert.type} alert-dismissible fade show mb-4`} role="alert">
              {getAlertIcon(alert.type)}
              {alert.message}
              <button 
                type="button" 
                className="btn-close" 
                onClick={() => setAlert({ show: false, type: '', message: '' })}
                aria-label="Close"
              ></button>
            </div>
          )}

          <div className="card shadow-sm">
            <div className="card-header bg-primary text-white">
              <div className="d-flex justify-content-between align-items-center">
                <h4 className="mb-0">
                  <FaBuilding className="me-2" />
                  Información de la Empresa
                </h4>
                <div>
                  {!isEditing && puedeEditarEmpresa ? (
                    <button
                      className="btn btn-light btn-sm"
                      onClick={() => setIsEditing(true)}
                    >
                      <FaEdit className="me-1" />
                      Editar
                    </button>
                  ) : isEditing ? (
                    <div className="btn-group">
                      <button
                        className="btn btn-success btn-sm"
                        onClick={handleSave}
                        disabled={saving || uploadingLogo}
                      >
                        {saving ? (
                          <>
                            <div className="spinner-border spinner-border-sm me-1" role="status"></div>
                            Guardando...
                          </>
                        ) : (
                          <>
                            <FaSave className="me-1" />
                            Guardar
                          </>
                        )}
                      </button>
                      <button
                        className="btn btn-secondary btn-sm"
                        onClick={handleCancel}
                        disabled={saving || uploadingLogo}
                      >
                        <FaTimes className="me-1" />
                        Cancelar
                      </button>
                    </div>
                  ) : null}
                  {!puedeEditarEmpresa && (
                    <span className="badge bg-light text-dark">
                      Solo admin
                    </span>
                  )}
                </div>
              </div>
            </div>

            <div className="card-body">
              <div className="row">
                {/* Columna izquierda - Logo */}
                <div className="col-lg-4 col-md-6 mb-4">
                  <div className="card h-100">
                    <div className="card-header">
                      <h6 className="mb-0">
                        <FaImage className="me-2" />
                        Logo de la Empresa
                      </h6>
                    </div>
                    <div className="card-body text-center">
                      {previewLogo ? (
                        <div className="position-relative">
                          <img
                            src={previewLogo}
                            alt="Logo de la empresa"
                            className="img-fluid rounded"
                            style={{ maxHeight: '200px', maxWidth: '100%' }}
                          />
                          {isEditing && (
                            <button
                              className="btn btn-danger btn-sm position-absolute top-0 end-0 m-2"
                              onClick={handleRemoveLogo}
                              title="Eliminar logo"
                            >
                              <FaTrash />
                            </button>
                          )}
                        </div>
                      ) : (
                        <div 
                          className="border border-dashed rounded d-flex align-items-center justify-content-center"
                          style={{ height: '200px', backgroundColor: '#f8f9fa' }}
                        >
                          <div className="text-muted">
                            <FaImage size={40} className="mb-2" />
                            <p>Sin logo</p>
                          </div>
                        </div>
                      )}

                      {isEditing && (
                        <div className="mt-3">
                          <label className="btn btn-outline-primary btn-sm">
                            <FaUpload className="me-1" />
                            {uploadingLogo ? 'Subiendo...' : 'Seleccionar Logo'}
                            <input
                              type="file"
                              accept="image/*"
                              onChange={handleLogoChange}
                              className="d-none"
                              disabled={uploadingLogo}
                            />
                          </label>
                          <small className="d-block text-muted mt-2">
                            Formatos: JPG, PNG, GIF. Máximo 5MB
                          </small>
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                {/* Columna derecha - Datos de la empresa */}
                <div className="col-lg-8 col-md-6">
                  <div className="row g-3">
                    {/* RUC */}
                    <div className="col-md-6">
                      <label className="form-label fw-bold">RUC *</label>
                      <input
                        type="text"
                        className="form-control"
                        name="ruc"
                        value={companyData.ruc}
                        onChange={handleInputChange}
                        disabled={!isEditing}
                        placeholder="Ingrese el RUC"
                      />
                    </div>

                    {/* Nombre Comercial */}
                    <div className="col-md-6">
                      <label className="form-label fw-bold">Nombre Comercial</label>
                      <input
                        type="text"
                        className="form-control"
                        name="nombreComercial"
                        value={companyData.nombreComercial}
                        onChange={handleInputChange}
                        disabled={!isEditing}
                        placeholder="Ingrese el nombre comercial"
                      />
                    </div>

                    {/* Razón Social */}
                    <div className="col-12">
                      <label className="form-label fw-bold">Razón Social *</label>
                      <input
                        type="text"
                        className="form-control"
                        name="name"
                        value={companyData.name}
                        onChange={handleInputChange}
                        disabled={!isEditing}
                        placeholder="Ingrese la razón social"
                      />
                    </div>

                    {/* Dirección */}
                    <div className="col-12">
                      <label className="form-label fw-bold">
                        <FaMapMarkerAlt className="me-1" />
                        Dirección
                      </label>
                      <textarea
                        className="form-control"
                        name="direccion"
                        value={companyData.direccion}
                        onChange={handleInputChange}
                        disabled={!isEditing}
                        rows="3"
                        placeholder="Ingrese la dirección completa"
                      />
                    </div>

                    {/* Distrito */}
                    <div className="col-md-4">
                      <label className="form-label fw-bold">Distrito</label>
                      <input
                        type="text"
                        className="form-control"
                        name="distrito"
                        value={companyData.distrito}
                        onChange={handleInputChange}
                        disabled={!isEditing}
                        placeholder="Ingrese el distrito"
                      />
                    </div>

                    {/* Provincia */}
                    <div className="col-md-4">
                      <label className="form-label fw-bold">Provincia</label>
                      <input
                        type="text"
                        className="form-control"
                        name="provincia"
                        value={companyData.provincia}
                        onChange={handleInputChange}
                        disabled={!isEditing}
                        placeholder="Ingrese la provincia"
                      />
                    </div>

                    {/* Departamento */}
                    <div className="col-md-4">
                      <label className="form-label fw-bold">Departamento</label>
                      <input
                        type="text"
                        className="form-control"
                        name="departamento"
                        value={companyData.departamento}
                        onChange={handleInputChange}
                        disabled={!isEditing}
                        placeholder="Ingrese el departamento"
                      />
                    </div>
                  </div>

                  {/* Información adicional */}
                  {!isEditing && (
                    <div className="mt-4">
                      <div className="alert alert-info">
                        <small>
                          <strong>Nota:</strong> Los campos marcados con (*) son obligatorios.
                          {puedeEditarEmpresa
                            ? ' Haga clic en "Editar" para modificar la información de la empresa.'
                            : ' Solo usuarios admin pueden modificar la información de la empresa.'}
                        </small>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Company;
